/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useMemo } from 'react';
import { Route, useRouteMatch } from 'react-router-dom';
import { HydratedRouteInterface } from '../types/routes';
import { useAppSelector } from '../reducers';
import { UpgradeRequired } from './upgrade-required';
import NotAuthorised from './not-authorised';
import Header from '@features/header';
import { getUserPermissionGroups } from '../selectors/initiative';
import { useSubscriptionCheckoutSuccess } from '../hooks/useSubscriptionCheckoutSuccess';
import { getRootConfig } from '@selectors/globalData';

interface AppRouteProps {
  route: HydratedRouteInterface;
  showHeader?: boolean;
}

export const AppRoute = (props: AppRouteProps) => {
  const { route, showHeader = false } = props;

  const match = useRouteMatch();
  useSubscriptionCheckoutSuccess();

  const rootConfig = useAppSelector(getRootConfig);
  const permissionGroups = useAppSelector(getUserPermissionGroups);
  const appId = route.appPermissionId;
  const hasAppPermission = useMemo(() =>
    !appId || permissionGroups.some(group => group.apps[appId] === true),
    [appId, permissionGroups]
  );

  if (!hasAppPermission) {
    return <>
      {showHeader ? <Route path='/:root?/:unused?' component={Header} /> : null}
      <NotAuthorised />
    </>;
  }

  if (route.canAccessFeaturePermission) {
    const canAccessFeature = route.canAccessFeaturePermission(rootConfig);
    if (!canAccessFeature) {
      return <>
        {showHeader ? <Route path='/:root?/:unused?' component={Header} /> : null}
        <UpgradeRequired />
      </>;
    }
  }

  return <route.component {...route} match={match} />;
}
